import AvsecAlertsAPIService from '../services/api/AvsecAlerts/ApiService'
import { EventDetailsPanel } from '../components/EventDetailsPanel/EventDetailsPanel'
import MapBox from './map'
import moment from 'moment'
import User from './user'
import striptags from 'striptags'
import { SETTINGS } from '../SETTINGS'
import { debounce } from 'lodash'
import { COUNTRY_DETAILS } from '../data/country-details'
import Airport from './airport'
import * as turf from '@turf/turf'
import ModifyAlertsLocation from './modifyAlertsLocation'
import { findIfAlertsAreInAirportRange } from '../services/EventsService'
import Sentry from './SantryIO'
import MixPanelService from '../services/MixPanelService'

export class AvsecAlertsModule {
    showAlerts = false
    eventsInitialized = false
    timeFrame = SETTINGS.FILTERS_DEFAULT_TIMEFRAME
    alerts = []
    latestAlerts = []
    filteredAlerts = []
    hasFirstTimeFetchedLatestAlerts = false
    isFetchingAlerts = true
    airports = []

    initialize(airports) {
        this.airports = airports
        User.getLocalUserSettings().then(settings => {
            let userFilters = JSON.parse(settings.filters)
            this.showAlerts = userFilters.enableAlerts && userFilters.enableAviationAlerts
            this.timeFrame = userFilters.timeFrame
            this.severity = userFilters.alertSeverities

            const todayMinus30days = moment().subtract(1, 'month')
            const todayMinus30daysInUTC = moment.utc(todayMinus30days).format()

            this.getAlerts(todayMinus30daysInUTC).then(alerts => {
                this.latestAlerts = alerts
                this.hasFirstTimeFetchedLatestAlerts = true
                this.startLatestAlertsObserver()
            }).finally(() => {
                this.hasFirstTimeFetchedLatestAlerts = true
            })
        })
    }

    async getAlerts(todayMinus30daysInUTC, maximumPeriodDate) {
        return await new Promise((resolve, reject) => {
            this.isFetchingAlerts = true
            AvsecAlertsAPIService.getAlerts(todayMinus30daysInUTC, maximumPeriodDate).then(avsecAlerts => {
                const alerts = avsecAlerts ?? []

                alerts.forEach(alert => {
                    if (alert?.AffectedLocation?.Coordinates?.length > 0) {
                        if (alert.AffectedLocation?.Coordinates?.[0] === 0 && alert.AffectedLocation?.Coordinates?.[1] === 0) {
                            alert.AffectedLocation = null
                        }
                    }

                    if (alert?.AffectedAirports?.length > 0 && (!alert.AffectedFIRs && !alert.AffectedLocation && !alert?.countriesAffectedAlerts)) {
                        alert.isAirportOnly = true
                    } else {
                        alert.isAirportOnly = false
                    }
                })

                const adjustedAlerts = alerts.map(alert => {
                    const re = new RegExp(String.fromCharCode(160), 'g')
                    alert.Heading = striptags(alert.Heading).replaceAll(re, '')
                    return alert
                })

                this.alerts = adjustedAlerts

                this.isFetchingAlerts = false

                resolve(adjustedAlerts)

                this.getCurrentAlertsAvailableCategories().then(servicesAndCategories => {
                    this.servicesAndCategories = servicesAndCategories

                    User.getLocalUserSettings().then(settings => {
                        let userFilters = JSON.parse(settings.filters)
                        this.severity = userFilters.alertSeverities
                        this.timeFrame = userFilters.timeFrame
    
                        this.filter(this.servicesAndCategories, this.timeFrame, maximumPeriodDate, this.severity)
                    })
                })


                if (!this.eventsInitialized) {
                    this.addEventsListeners()
                    this.eventsInitialized = true
                }
            }).catch(error => {
                Sentry.captureException(error)
                reject(error)
            })
        })
    }

    async getModuleAlerts() {
        const alerts = await new Promise((resolve) => {
            const observerInterval = window.setInterval(() => {
                if (!this.isFetchingAlerts) {
                    resolve(this.alerts)
                    window.clearInterval(observerInterval)
                }
            }, 25)
        })

        return alerts
    }

    async getModuleAirportAlerts(airportICAO) {
        const aviationAlerts = await this.getModuleLatestAlerts()
        const airportDetails = Airport.getAirportByICAOCode(airportICAO)
        const airportPoint = turf.point([Number(airportDetails.Lng), Number(airportDetails.Lat)])
        const airportAlertsToDisplay = aviationAlerts.filter(alert => {
            if (alert?.AffectedAirports?.includes(airportICAO)) return true

            // If an airport is under the affected location of an alert (its radius), we need to display it as well
            if (!!alert.AffectedLocation && !!alert.AffectedLocation.Radius) {
                const alertPoint = turf.point([Number(alert.AffectedLocation?.Coordinates?.[0]), Number(alert.AffectedLocation?.Coordinates?.[1])])

                // Check if the distance between the airportPoint and alert point is less or equal to the radius of the alert
                const distance = turf.distance(airportPoint, alertPoint, { units: 'kilometers' })
                return distance <= alert.AffectedLocation.Radius
            }

            return false
        })

        return airportAlertsToDisplay
    }

    async getModuleAirportAlertsInLast72Hours(airportICAO) {
        const aviationAlerts = await this.getModuleLatestAlerts()
        const airportDetails = Airport.getAirportByICAOCode(airportICAO)
        const airportPoint = [airportDetails.Lat, airportDetails.Lng]
        const airportAlertsToDisplay = aviationAlerts.filter(alert => {
            const alertDate = moment(alert.Date)
            const todayMinus72Hours = moment().subtract(72, 'hours')
            if (alertDate.isAfter(todayMinus72Hours)) {
                if (alert?.AffectedAirports?.includes(airportICAO)) return true

                if (!!alert.AffectedLocation) {
                    let distanceFromAirport = findIfAlertsAreInAirportRange(alert.AffectedLocation, airportPoint, 100)
                    if (distanceFromAirport && distanceFromAirport <= 100) {
                        return true
                    }
                }
            }
            return false
        })
        return airportAlertsToDisplay
    }

    // We want to get the alerts for the city an airport belongs to, still our system works in a way that we consider as city alerts as a radius around the airport
    async getModuleCityAlerts(airportICAO) {
        const aviationAlerts = await this.getModuleLatestAlerts()
        const airportDetails = Airport.getAirportByICAOCode(airportICAO)
        const airportPoint = turf.point([Number(airportDetails.Lng), Number(airportDetails.Lat)])
        const airportAlertsToDisplay = aviationAlerts.filter(alert => {
            if (!!alert.AffectedLocation && !!alert.AffectedLocation.Radius) {
                const alertPoint = turf.point([Number(alert.AffectedLocation?.Coordinates?.[0]), Number(alert.AffectedLocation?.Coordinates?.[1])])

                // Check if the distance between the airportPoint and alert point is less or equal to the radius of the alert
                const distance = turf.distance(airportPoint, alertPoint, { units: 'kilometers' })
                return distance <= alert.AffectedLocation.Radius
            }

            return false
        })

        return airportAlertsToDisplay
    }

    // We want to get the alerts for the country
    async getModuleCountryAlerts(countryCode3) {
        const aviationAlerts = await this.getModuleLatestAlerts()
        const airportAlertsToDisplay = aviationAlerts.filter(alert => {
            const countryAffected = alert?.CountriesAffected?.filter(affectedCountry => affectedCountry.CountryCodeAlpha3 === countryCode3)

            if (countryAffected.length > 0) {
                return true
            }
            return false
        })
        return airportAlertsToDisplay
    }

    async getModuleFIRAlerts(firICAO) {
        const aviationAlerts = await this.getModuleLatestAlerts()
        const firAlertsToDisplay = aviationAlerts.filter(alert => alert.AffectedFIRs.includes(firICAO))
        return firAlertsToDisplay
    }

    async getModuleLatestAlerts() {
        const latestAlerts = await new Promise((resolve) => {
            const observerInterval = window.setInterval(() => {
                if (this.hasFirstTimeFetchedLatestAlerts) {
                    resolve(this.latestAlerts)
                    window.clearInterval(observerInterval)
                }
            }, 25)
        })

        return latestAlerts
    }

    async getCurrentAlertsAvailableCategories() {
        let userSettings = await User.getLocalUserSettings()
        const parsedUserFilters = JSON.parse(userSettings.filters)
        const userAlertFilters = parsedUserFilters.aviationFilters

        const aviationFilters = await new Promise((resolve) => {
            const observerInterval = window.setInterval(() => {
                if (!this.isFetchingAlerts) {
                    let aviationFilters = {
                        enabled: true,
                        categories: []
                    }

                    this.alerts.forEach(alert => {
                        alert.Categories?.forEach(category => {
                            if (aviationFilters.categories.findIndex((existingCategory) => existingCategory.name === category) === -1) {
                                aviationFilters.categories.push({
                                    name: category,
                                    enabled: true
                                })
                                // If user had that category defined, then overide the value
                                const existingUserCategory = userAlertFilters[category]
                                if (existingUserCategory !== undefined) {
                                    aviationFilters.categories[aviationFilters.categories.length - 1].enabled = existingUserCategory.enabled
                                }
                            }
                        })
                    })

                    resolve(aviationFilters)

                    window.clearInterval(observerInterval)
                }
            }, 100)
        })

        return aviationFilters
    }

    startLatestAlertsObserver() {
        const ONE_MINUTE = 1000 * 60
        window.setInterval(() => {
            const todayMinus30days = moment().subtract(1, 'month')
            const todayMinus30daysInUTC = moment.utc(todayMinus30days).format()

            this.getAlerts(todayMinus30daysInUTC).then(alerts => {
                this.latestAlerts = alerts
            })
        }, ONE_MINUTE)
    }

    filter(alertFilters, timeFrame, maximumPeriodDate, alertSeverities) {
        if (timeFrame !== undefined) this.currentTimeFrame = timeFrame
        if (maximumPeriodDate === undefined) maximumPeriodDate = moment()

        const filteredAlerts = []
        const beginningOfTheRange = moment(maximumPeriodDate).subtract(this.currentTimeFrame, 'days')

        this.alerts.forEach(alert => {
            if (moment(alert.Date).isAfter(beginningOfTheRange)) {
                const isAnyAlertCategoryEnabled = alert.Categories.some(alertCategory => alertFilters?.categories?.find(filterCategory => filterCategory.name === alertCategory && filterCategory.enabled))
                if (isAnyAlertCategoryEnabled) {
                    if (!!alert?.Severity) {
                        if (alertSeverities[alert?.Severity]) {
                            filteredAlerts.push(alert)
                        }
                    } else {
                        filteredAlerts.push(alert)
                    }
                }
            }
        })
        this.filteredAlerts = filteredAlerts
        this.display(filteredAlerts)
    }

    getFeatureCollection(alerts) {
        const featureCollection = {
            type: 'FeatureCollection',
            features: []
        }

        for (let alert of alerts) {
            const properties = {
                ['longitude']: alert.Lng,
                ['latitude']: alert.Lat,
                ['isoCode2']: alert.IsoCode,
                ['country']: alert.SelectedCountry,
                ['isBreaking']: alert.IsBreaking,
                ['isAirportOnly']: alert.isAirportOnly,
                ['iconCount']: alert.iconCount > 10 ? '10+' : (alert.iconCount || null),
                ['iconCountAndNotBreaking']: !alert.IsBreaking && alert.iconCount > 1,
                ['iconCountAndBreaking']: alert.IsBreaking && alert.iconCount > 1,
                isLive: (() => {
                    if (!alert.IsBreaking && alert.IsPreviousBreaking) {
                        return 'No'
                    } else {
                        return moment(alert.Date).isSameOrBefore(moment().subtract(24, 'hours')) ? 'No' : 'Yes'
                    }
                })()
            }

            const point = {
                type: 'Point',
                coordinates: [alert.Lng, alert.Lat]
            }

            const feature = {
                type: 'Feature',
                id: alert.NodeId,
                geometry: point,
                properties: properties
            }

            featureCollection.features.push(feature)
        }

        return featureCollection
    }

    getDisplayAlerts(alerts) {
        const alertsToDisplay = []

        alerts.forEach(alert => {
            const latLngData = alert?.AffectedLocation?.Coordinates
            if (latLngData?.length > 0) {
                const latitude = latLngData[1]
                const longitude = latLngData[0]
                alert.AffectedLocation.latlng = { lng: longitude, lat: latitude }
            }

            // If the alert is not for a specific location, then we need to display it for all the countries
            if (!alert.AffectedLocation && !alert.isAirportOnly) {
                alert.CountriesAffected.forEach(country => {
                    // const isAlertForCountryAlreadyIncluded = alertsToDisplay.some(displayAlert => displayAlert.CountryCodeAlpha2 === country.CountryCodeAlpha2)

                    if (country.CountryCodeAlpha2 !== null) {
                        // Simplified version that contains only a minimum amount of data
                        const countryDetails = COUNTRY_DETAILS.find(details => details.ISO_3166_1_ALPHA_2 === country.CountryCodeAlpha2)
                        alertsToDisplay.push({
                            IsoCode: country.CountryCodeAlpha2,
                            SelectedCountry: country.Name,
                            Lng: countryDetails.centralLng,
                            Lat: countryDetails.centralLat,
                            NodeId: alert.NodeId,
                            IsBreaking: alert.IsBreaking,
                            Date: alert.Date,
                            isAirportOnly: alert.isAirportOnly
                        })
                    }
                })
            }
        })

        return alertsToDisplay
    }

    getAffectedLocationCollection(alerts, avsecAlertsFeatureCollection) {
        const featureCollection = {
            type: 'FeatureCollection',
            features: []
        }

        alerts?.forEach(alert => {
            const latLngData = alert?.AffectedLocation?.latlng
            const radius = alert?.AffectedLocation?.Radius
            // Specific location items are also valid if they have no radius
            if (latLngData && (radius || radius === 0)) {
                const properties = {
                    ['NodeId']: alert.NodeId,
                    'type': 'location',
                    'isBreaking': alert.IsBreaking,
                    'isAirportOnly': alert.isAirportOnly,
                    ['iconCountAndNotBreaking']: false,
                    ['iconCountAndBreaking']: false,
                    isLive: (() => {
                        if (!alert.IsBreaking && alert.IsPreviousBreaking) {
                            return 'No'
                        } else {
                            return moment(alert.Date).isSameOrBefore(moment().subtract(24, 'hours')) ? 'No' : 'Yes'
                        }
                    })()
                }

                if (radius > 0) {
                    const point = turf.point([latLngData.lng, latLngData.lat])
                    const buffered = turf.buffer(point, radius, { units: 'kilometers' })

                    buffered.properties = properties
                    featureCollection.features.push(buffered)
                }

                avsecAlertsFeatureCollection?.features?.push({
                    type: 'Feature',
                    id: alert.NodeId,
                    geometry: {
                        type: 'Point',
                        coordinates: [latLngData.lng, latLngData.lat]
                    },
                    properties: properties
                })
            }
        })
        return featureCollection
    }

    getAirportOnlyAlerts(alerts, avsecAlertsFeatureCollection) {

        alerts?.forEach(alert => {
            if (alert.isAirportOnly) {
                alert.AffectedAirports.forEach(airportICAO => {
                    let airport = this.airports.find(airport => airport.ICAO === airportICAO)
                    if (airport !== undefined) {
                        const properties = {
                            ['NodeId']: alert.NodeId,
                            'type': 'location',
                            'isBreaking': alert.IsBreaking,
                            ['iconCountAndNotBreaking']: false,
                            ['iconCountAndBreaking']: false,
                            ['isAirportOnly']: alert.isAirportOnly,
                            isLive: (() => {
                                if (!alert.IsBreaking && alert.IsPreviousBreaking) {
                                    return 'No'
                                } else {
                                    return moment(alert.Date).isSameOrBefore(moment().subtract(24, 'hours')) ? 'No' : 'Yes'
                                }
                            })(),
                            ['longitude']: airport.Lng,
                            ['latitude']: airport.Lng,
                            ['isoCode2']: airport.Isocode
                        }

                        avsecAlertsFeatureCollection?.features?.push({
                            type: 'Feature',
                            id: alert.NodeId,
                            geometry: {
                                type: 'Point',
                                coordinates: [airport.Lng + 0.0003, airport.Lat + 0.0003]
                            },
                            properties: properties
                        })
                    } else {
                        console.warn('Found an airport associated to an alert that is not in the airports list')
                    }
                })
            }

        })
    }

    display(alerts) {
        // AVSEC alerts are alerts issues towards countries, we can have multiple alerts for each country, so on the map
        // we really just need to display one icon, instead of many overlapping and once clicked retrieve the list of all of them
        const alertsToDisplay = this.getDisplayAlerts(alerts)

        const coordinatesMap = new Map()

        for (const record of alertsToDisplay) {
            const coordinates = `${record.Lng},${record.Lat}`
            if (coordinatesMap.has(coordinates)) {
                const existingRecord = coordinatesMap.get(coordinates)

                existingRecord.iconCount = (existingRecord.iconCount || 1) + 1
            } else {
                coordinatesMap.set(coordinates, record)
            }
        }

        const deduplicatedData = Array.from(coordinatesMap.values())

        // let uniqueCountryList = uniqBy(alertsToDisplay, (record) => `${record.Lng}-${record.Lat}`)
        let avsecAlertsFeatureCollection = this.getFeatureCollection(deduplicatedData)
        const avsecAlertsAffectedLocationCollection = this.getAffectedLocationCollection(alerts, avsecAlertsFeatureCollection)
        this.getAirportOnlyAlerts(alerts, avsecAlertsFeatureCollection)


        // TODO: need to find an issue of the Style are still not loaded, seems we have some racing conditions with the MapBox and the API time
        // Will probably need to apply the fix also to the International SOS alerts
        const alertLayer = MapBox.map.getSource('avsec-alerts')
        const alertbufferLayer = MapBox.map.getSource('avsec-alerts-buffer')

        if (alertbufferLayer !== undefined) {
            alertbufferLayer.setData(avsecAlertsAffectedLocationCollection)
        } else {
            window.setTimeout(() => {
                MapBox.map.addSource('avsec-alerts-buffer', {
                    type: 'geojson',
                    data: avsecAlertsAffectedLocationCollection
                })

                MapBox.map.addLayer({
                    id: 'avsec-alerts-buffer',
                    type: 'fill',
                    source: 'avsec-alerts-buffer',
                    layout: {
                        'visibility': this.showAlerts ? 'visible' : 'none'
                    },
                    paint: {
                        'fill-color': [
                            'case',
                            ['boolean', ['get', 'isBreaking'], true], 'rgba(237, 130, 39, 0.15)',
                            'rgba(212, 67, 51, 0.15)'
                        ]

                    }
                })
            }, 2000)
        }

        if (alertLayer !== undefined) {
            alertLayer.setData(avsecAlertsFeatureCollection)
        } else {
            window.setTimeout(() => {
                MapBox.map.addSource('avsec-alerts', {
                    'type': 'geojson',
                    'data': avsecAlertsFeatureCollection
                })

                MapBox.map.addLayer({
                    id: 'avsec-alerts',
                    type: 'symbol',
                    source: 'avsec-alerts',
                    paint: {
                        'text-color': '#ffffff',
                        'text-opacity': 1,
                        'text-halo-color': 'white',
                        'text-halo-width': 1.3,
                    },
                    layout: {
                        'visibility': this.showAlerts ? 'visible' : 'none',
                        // 'icon-image': 'aviation-alert', 
                        'icon-image': [
                            'case',

                            ['all', ['boolean', ['get', 'iconCountAndBreaking'], true], ['==', ['get', 'isLive'], 'No']], 'alert-cluster-breaking',
                            ['all', ['boolean', ['get', 'iconCountAndBreaking'], true], ['==', ['get', 'isLive'], 'Yes']], 'alert-cluster-breaking-live',

                            ['all', ['boolean', ['get', 'isBreaking'], true], ['==', ['get', 'isLive'], 'No'], ['==', ['get', 'isAirportOnly'], false]], 'breaking-alert',
                            ['all', ['boolean', ['get', 'isBreaking'], true], ['==', ['get', 'isLive'], 'Yes'], ['==', ['get', 'isAirportOnly'], false]], 'breaking-alert-live',

                            ['all', ['boolean', ['get', 'isAirportOnly'], true], ['==', ['get', 'isLive'], 'No']], 'airport-only-alert',
                            ['all', ['boolean', ['get', 'isAirportOnly'], true], ['==', ['get', 'isLive'], 'Yes']], 'airport-only-alert-live',

                            ['all',
                                ['!', ['boolean', ['get', 'isBreaking'], true]],
                                ['!', ['boolean', ['get', 'isAirportOnly'], true]],
                                ['!', ['boolean', ['get', 'iconCountAndBreaking'], true]],
                                ['!', ['boolean', ['get', 'iconCountAndNotBreaking'], true]],
                                ['==', ['get', 'isLive'], 'Yes']
                            ], 'aviation-alert-live',

                            ['all', ['boolean', ['get', 'iconCountAndNotBreaking'], true], ['==', ['get', 'isLive'], 'No']], 'alert-cluster',
                            ['all', ['boolean', ['get', 'iconCountAndNotBreaking'], true], ['==', ['get', 'isLive'], 'Yes']], 'alert-cluster-live',

                            'aviation-alert'
                        ],
                        'icon-allow-overlap': true,
                        'text-allow-overlap': true,
                        'text-field': ['case', ['!=', ['get', 'iconCount'], null], ['get', 'iconCount'], ''],
                        'icon-size': ['interpolate', ['linear'], ['zoom'], 3.5, 0.16, 4.5, 0.23, 7, 0.33],
                        'text-size': ['interpolate', ['linear'], ['zoom'], 0, 10, 3.5, 12, 4.5, 14, 7, 16],
                        'text-font': [
                            'Open Sans Semibold',
                            'Arial Unicode MS Bold'
                        ],
                        'text-anchor': 'center',
                    }
                })

                MapBox.map.addLayer({
                    'id': 'avsec-alert-layer-with-pulsing-dot',
                    'type': 'symbol',
                    'source': 'avsec-alerts',
                    'layout': {
                        'visibility': this.showAlerts ? 'visible' : 'none',
                        'icon-image': [
                            'match',
                            ['get', 'isLive'],
                            'Yes', 'pulsing-dot',
                            ''
                        ],
                        'icon-allow-overlap': true,
                        'text-allow-overlap': true,
                        'icon-size': ['interpolate', ['linear'], ['zoom'], 3.5, 0.36, 4.5, 0.43, 7, 0.53]
                    }
                })
            }, 2000)
        }

        // We need to call this function to make sure that the alerts are not overlapping
        ModifyAlertsLocation.modifyLocation()
    }

    // We need a function that will display an alert on the map regardless of the filter or if they were disabled
    // E.g. user's filter don't show a specific alert, but user clicks from another module to see details of it
    showCountryAlerts(isoCode2) {
        // For simplicity we are going to assume that the alert is always not visible, so if it is not visible we
        // will display it, if it is visible then we will just have another alert on top of it, visually it should be fine
        const allAvsecAlertsOfTheCountry = this.getCountryAvsecAlerts(isoCode2)
        if (allAvsecAlertsOfTheCountry.length > 0) {
            const alertsToDisplay = this.getDisplayAlerts(allAvsecAlertsOfTheCountry)
            const avsecAlertsFeatureCollection = this.getFeatureCollection(alertsToDisplay)
            const singleAlertLayer = MapBox.map.getSource('avsec-single-alert')

            if (singleAlertLayer !== undefined) {
                singleAlertLayer.setData(avsecAlertsFeatureCollection)
            } else {
                MapBox.map.addSource('avsec-single-alert', {
                    'type': 'geojson',
                    'data': avsecAlertsFeatureCollection
                })

                MapBox.map.addLayer({
                    id: 'avsec-single-alert',
                    type: 'symbol',
                    source: 'avsec-single-alert',
                    layout: {
                        'visibility': this.showAlerts ? 'visible' : 'none',
                        'icon-image': 'aviation-alert',
                        'icon-allow-overlap': true,
                        'text-allow-overlap': true,
                        'icon-size': ['interpolate', ['linear'], ['zoom'], 3.5, 0.16, 4.5, 0.23, 7, 0.33]
                    }
                })
            }

            MapBox.map.setLayoutProperty('avsec-single-alert', 'visibility', 'visible')

            EventDetailsPanel.showAviationCountryAlerts(allAvsecAlertsOfTheCountry, isoCode2)
        }
    }

    showAlert(alertId, type = '') {
        const alert = this.alerts.find(alert => alert.NodeId === alertId)
         MixPanelService.track('Avsec Alert Clicked', { 
            Id: alert.NodeId,
            Title: alert.Title,
            AirportOnlyAlert: alert.isAirportOnly,
            BreakingAlert: alert.IsBreaking,
            CountriesAffected: alert.CountriesAffected?.map(country => country.Name)?.join(', ') || '',
            AffectedLocation: alert.AffectedLocation?.Coordinates?.length > 0 ? true : false
        })
        EventDetailsPanel.showAviationAlert(alert, type)
    }

    showAirportAlert(alertId, airportICAO) {
        const alert = this.alerts.find(alert => alert.NodeId === alertId)
        const airportDetails = Airport.getAirportByICAOCode(airportICAO)
        EventDetailsPanel.showAviationAirportAlert(alert, airportDetails)
    }

    show() {
        this.showAlerts = true
        MapBox.map.setLayoutProperty('avsec-alerts', 'visibility', 'visible')
        MapBox.map.setLayoutProperty('avsec-alerts-buffer', 'visibility', 'visible')
        MapBox.map.setLayoutProperty('avsec-alert-layer-with-pulsing-dot', 'visibility', 'visible')
        this.debounceSavingUserSettings()
    }

    hide() {
        this.showAlerts = false
        MapBox.map.setLayoutProperty('avsec-alerts', 'visibility', 'none')
        MapBox.map.setLayoutProperty('avsec-alerts-buffer', 'visibility', 'none')
        MapBox.map.setLayoutProperty('avsec-alert-layer-with-pulsing-dot', 'visibility', 'none')
        this.debounceSavingUserSettings()
    }

    debounceSavingUserSettings = debounce(this.saveUserAlertsVisibilitySetting, 1500)

    saveUserAlertsVisibilitySetting() {
        User.getLocalUserSettings().then(settings => {
            let userFilters = JSON.parse(settings.filters)
            userFilters.enableAviationAlerts = this.showAlerts
            User.updateUserFilters(userFilters)
        })
    }

    getCountryAvsecAlerts(isoCode2) {
        let alert = this.alerts.filter(alert => {
            let anyCountriesAffectedInTheAlert = false
            alert.CountriesAffected.forEach(country => {
                if (country.CountryCodeAlpha2 === isoCode2) {
                    anyCountriesAffectedInTheAlert = true
                }
            })
            return anyCountriesAffectedInTheAlert
        })
        return alert.sort((a, b) => moment(b.Date).diff(moment(a.Date)))
    }

    getAffectedCountryAvsecAlerts(isoCode2) {
        let alert = this.filteredAlerts.filter(alert => {
            if (!alert.AffectedLocation && !alert.isAirportOnly) {
                let anyCountriesAffectedInTheAlert = false
                alert.CountriesAffected.forEach(country => {
                    if (country.CountryCodeAlpha2 === isoCode2) {
                        anyCountriesAffectedInTheAlert = true
                    }
                })
                return anyCountriesAffectedInTheAlert
            }
        })
        return alert.sort((a, b) => moment(b.Date).diff(moment(a.Date)))
    }

    addEventsListeners() {
        MapBox.map.on('mouseenter', 'avsec-alerts', () => MapBox.map.getCanvas().style.cursor = 'pointer')
        MapBox.map.on('mouseleave', 'avsec-alerts', () => MapBox.map.getCanvas().style.cursor = 'inherit')
        MapBox.map.on('mousemove', 'avsec-alerts-buffer', () => MapBox.map.getCanvas().style.cursor = 'pointer')
        MapBox.map.on('mouseleave', 'avsec-alerts-buffer', () => MapBox.map.getCanvas().style.cursor = 'inherit')
    }
}

const AvsecAlerts = new AvsecAlertsModule()
export default AvsecAlerts
